import { useCallback, useMemo } from "react"

import { format } from "date-fns"

import { useDataLayer } from "@/features/DataLayer"
import { calculateDaysToArrival } from "@/features/DataLayer/helpers"
import { usePartnerCode } from "@/features/PartnerCode"
import { useSelectedResort } from "@/hooks"
import {
  useCategoryParam,
  useDateParam,
  useResortParam,
} from "@/hooks/useSkipassFlow"
import useCategories from "@/queries/hooks/useWebCategories"
import getSeason from "@/utils/getSeason"

type DetailCompleteProduct = {
  id: string
  name: string
  price: number
  currencyCode: string
  category: "SkiPass" | "Ticket"
}

export const useDetailCompleteEvent = () => {
  const pushDetailComplete = useDataLayer("detailComplete", { sendOnce: true })

  const { startDate } = useDateParam()
  const { resortExternalId } = useResortParam()
  const { selectedResort } = useSelectedResort()
  const { partnerCode } = usePartnerCode()
  const { categoryId } = useCategoryParam()
  const { data: categories } = useCategories(resortExternalId, startDate)

  const season = useMemo(() => getSeason(startDate), [startDate])

  const productBlock = useMemo(() => {
    if (!categories || categoryId === undefined) return ""
    const category = categories.find((c) => c.id === categoryId)
    return category?.name ?? ""
  }, [categories, categoryId])

  const runDetailComplete = useCallback(
    (product: DetailCompleteProduct) => {
      pushDetailComplete({
        ecommerce: {
          detail: {
            actionField: { list: null },
            products: [
              {
                id: product.id,
                product_id: "",
                name: product.name,
                price: product.price.toFixed(2),
                brand: selectedResort?.name ?? null,
                category: product.category,
                coupon: partnerCode,
                variant: null,
                quantity: null,
                position: "",
                dimension16: season,
                dimension17: null,
                dimension18: calculateDaysToArrival(startDate).toString(),
                dimension20: format(startDate, "yyyy-MM-dd"),
              },
            ],
          },
          currencyCode: product.currencyCode,
        },
        ProductBlock: productBlock,
      })
    },
    [
      pushDetailComplete,
      productBlock,
      season,
      startDate,
      selectedResort,
      partnerCode,
    ]
  )

  return runDetailComplete
}
